import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, CheckCircle, ArrowRight, Phone, Mail, MessageCircle,
  Clock, MapPin, Users, Building2, ChevronRight, XCircle, Plane,
  FileCheck, FileText, BadgeCheck, Smartphone, Camera, CreditCard,
  Sparkles, Globe2, Calendar,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';
import { useContactChannel } from '@/hooks/useContactChannel';

/**
 * /saudi-arabia/tourist-visa
 * ────────────────────────────────────────────────────────────────────────
 * SEO landing page targeting "Saudi Tourist Visa for Indians" and related
 * queries: Saudi tourist e-visa India, multiple entry tourist visa Saudi,
 * Saudi tourist visa processing time India, Saudi visa stamping India,
 * apply Saudi tourist visa online, etc.
 *
 * Mirrors the SaudiBusinessVisitVisaPage layout/component structure for
 * design consistency. No pricing/fee figures (per editorial decision).
 */

const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const ELIGIBILITY = [
  { label: 'Valid Indian Passport', detail: 'Minimum 6 months validity beyond intended entry date, with at least 2 blank pages.' },
  { label: 'Age Requirement', detail: 'At least 18 years old (minors must accompany a legal guardian).' },
  { label: 'Clean Criminal Record', detail: 'No criminal convictions or pending cases in India or any country.' },
  { label: 'Travel Purpose', detail: 'Leisure, tourism, family visits, or Umrah (non-Hajj season only). Saudi tourist visa without sponsor for Indians available.' },
  { label: 'Health Insurance', detail: 'Mandatory medical insurance — bundled automatically with the e-Visa application.' },
  { label: 'Financial Capability', detail: 'Sufficient funds for accommodation and expenses. Minimum bank balance for Saudi tourist visa from India: USD 750 quarterly average.' },
  { label: 'No Hajj on Tourist Visa', detail: 'Attempting Hajj on a tourist visa is strictly prohibited — requires a separate Hajj permit.' },
];

const VISA_OPTIONS = [
  {
    icon: Sparkles,
    badge: 'Most Popular',
    badgeTone: 'bg-amber-100 text-amber-800',
    title: '1. Saudi Tourist Visa 1 Year Validity (e-Visa)',
    intro: 'The fastest and most convenient option for Indian tourists — apply Saudi tourist visa online via visa.visitsaudi.com.',
    specs: [
      { k: 'Visa Type', v: 'Electronic e-Visa (online application)' },
      { k: 'Validity Period', v: '1 year validity (365 days) from issuance' },
      { k: 'Duration of Stay', v: 'Up to 90 days per visit' },
      { k: 'Entries Allowed', v: 'Multiple entry — unlimited within validity' },
      { k: 'Processing Time', v: '5 minutes – 24 hours' },
      { k: 'Application Method', v: 'Fully online via visa.visitsaudi.com' },
      { k: 'Best For', v: 'Frequent travellers, family visits, leisure tourism, Umrah (non-Hajj)' },
    ],
    benefits: [
      'Multiple entry — unlimited re-entries within 1-year validity',
      '90 days per stay — longer than single-entry visas',
      'Fastest processing — 5 minutes to 24 hours approval',
      'Mandatory medical insurance bundled with application',
      'Digital format — printable e-Visa PDF, no physical stamp',
    ],
  },
  {
    icon: Plane,
    badge: 'On Arrival',
    badgeTone: 'bg-emerald-100 text-emerald-800',
    title: '2. Saudi E-Visa for Indian Citizens with US/UK Visa (Visa on Arrival)',
    intro: 'Available for Indian nationals holding valid (used) US, UK or Schengen visas — or GCC residency.',
    specs: [
      { k: 'Eligibility', v: 'US/UK/Schengen visa holders (used) or GCC residents' },
      { k: 'Validity', v: '90 days from entry date' },
      { k: 'Entries', v: 'Single entry' },
      { k: 'Processing Time', v: '5–10 minutes at airport' },
      { k: 'Best For', v: 'Urgent travel, existing visa holders' },
    ],
    benefits: [
      'No advance application required',
      'Approved at the immigration counter on arrival',
      'Ideal for last-minute leisure trips',
    ],
  },
  {
    icon: BadgeCheck,
    badge: 'Embassy Route',
    badgeTone: 'bg-slate-200 text-slate-800',
    title: '3. Saudi Visa Stamping India (Sticker Visa)',
    intro: 'Traditional sticker visa applied through Tasheer / Royal Saudi Embassy — single-entry, 30-day stay.',
    specs: [
      { k: 'Visa Type', v: 'Single-entry sticker visa (physical stamp)' },
      { k: 'Validity Period', v: '3 months from issuance' },
      { k: 'Duration of Stay', v: 'Up to 30 days per visit' },
      { k: 'Entries Allowed', v: '1 entry only' },
      { k: 'Processing Time', v: '7–15 working days' },
      { k: 'Application Method', v: 'Offline — Tasheer appointment + embassy submission' },
      { k: 'Best For', v: 'Applicants ineligible for e-Visa or with specific embassy requirements' },
    ],
    benefits: [
      'Step 1: Book Tasheer appointment (1–3 days)',
      'Step 2: Tasheer biometrics visit (1 day, 15–30 minutes)',
      'Step 3: Embassy processing & security clearance (5–15 days)',
      'Step 4: Passport collection after approval (1–2 days)',
    ],
  },
];

const DOCUMENT_GROUPS = [
  {
    icon: FileText,
    title: '1. Original Passport Requirements',
    points: [
      'Original Indian passport valid for minimum 6 months beyond intended travel date',
      'At least 2 blank pages for any stamps (sticker visa)',
      'Passport must be undamaged — no fraying, tears, or alterations',
      'Photocopies of data page, all visa/stamp pages, first and last pages',
      'Old passport (if available) with any previous Saudi visas',
    ],
  },
  {
    icon: Camera,
    title: '2. Passport-Size Photograph',
    points: [
      'Recent passport-sized photograph with white background',
      'Digital upload: 200 × 200 pixels, 5–100 KB (for e-Visa)',
      'Taken within the last 6 months',
      'Full frontal view, ~80% face coverage, neutral expression',
      'No eyeglasses or headwear (except for religious reasons)',
    ],
  },
  {
    icon: Plane,
    title: '3. Confirmed Return Flight Ticket',
    points: [
      'Confirmed round-trip flight booking or e-ticket',
      'Must show entry and exit dates from Saudi Arabia',
      'Return ticket mandatory — one-way tickets are not accepted',
    ],
  },
  {
    icon: Building2,
    title: '4. Confirmed Hotel Booking or Host Invitation',
    points: [
      'Confirmed hotel accommodation booking in Saudi Arabia, OR',
      'Host invitation letter from a Saudi resident (with Iqama copy)',
    ],
  },
  {
    icon: Shield,
    title: '5. Travel Insurance (Mandatory)',
    points: [
      'Valid medical insurance recognised in Saudi Arabia',
      'Included automatically with the e-Visa application (no separate purchase needed)',
      'Covers emergency medical treatment, hospitalisation and COVID-19 protocols',
    ],
  },
  {
    icon: FileCheck,
    title: '6. Proof of Financial Stability',
    points: [
      'Bank statement for the last 3 months — original, signed, stamped by the bank',
      'Minimum bank balance: USD 750 quarterly average',
      'Proof of employment or income documents',
      'Self-employed: business registration certificate or tax returns',
    ],
  },
];

const APPLICATION_PHASES = [
  {
    phase: 'Phase 1 — Pre-Application Preparation',
    duration: '1 day',
    steps: [
      'Check Saudi tourist visa eligibility for Indian citizens — confirm you meet all criteria for the visa without sponsor route.',
      'Gather every document from the checklist above (passport, photo, flight, hotel, bank statement).',
      'Click the "Contact" button or call our experts for a free document review before you apply.',
    ],
  },
  {
    phase: 'Phase 2 — Apply Saudi Tourist Visa Online',
    duration: '~30 minutes',
    steps: [
      'Access the official portal at visa.visitsaudi.com.',
      'Complete the online form: personal details, passport, travel plans, accommodation.',
      'Pay the official e-Visa fee using credit/debit card or payment wallet.',
    ],
  },
  {
    phase: 'Phase 3 — e-Visa Approval',
    duration: '5 minutes – 24 hours',
    steps: [
      'Wait for approval — average approval window is 2–5 hours.',
      'Download the printable e-Visa PDF from the email confirmation or portal.',
      'Carry the printed e-Visa with you for immigration on arrival in Saudi Arabia.',
    ],
  },
];

const TIMELINE = [
  { phase: 'e-Visa (Online)', activity: 'Apply Saudi tourist visa from India online', duration: '5 min – 24 hr', notes: 'Recommended path for most travellers' },
  { phase: 'Visa on Arrival', activity: 'US / UK / Schengen / GCC residents', duration: '5–10 min', notes: 'Processed at the airport' },
  { phase: 'Sticker Visa', activity: 'Tasheer + Embassy submission', duration: '7–15 working days', notes: 'Slower; for ineligible applicants' },
];

const WHY_ZAFCO = [
  { label: 'MEA-Approved Recruitment Agency', detail: `Government of India approved with RC: ${MEA_REG}` },
  { label: 'Saudi Embassy Empanelled', detail: 'Direct partnership with the Royal Embassy of Saudi Arabia for visa application and passport submission.' },
  { label: '15+ Years Experience', detail: 'Over 10,000 successful placements and 400,000+ visas processed across 50+ countries.' },
  { label: '98% Success Rate', detail: 'Authorised Saudi tourist visa agents in India with minimal rejection rate.' },
  { label: 'Deep Saudi Tourism Expertise', detail: '15+ years specialising in Saudi tourist visa processing — e-Visa, Visa on Arrival, sticker visa.' },
  { label: 'Unmatched Documentation Precision', detail: 'Experts verify EVERY document before submission to prevent rejection.' },
  { label: 'Saudi Visa Consultants in South Delhi', detail: 'Among the leading Saudi visit visa stamping agents in Delhi — visit our New Friends Colony office.' },
  { label: 'Hassle-Free Document Pickup', detail: 'Free pickup from New Delhi and pan-India locations; end-to-end assistance.' },
];

const FAQS = [
  {
    q: 'Can Indian nationals apply for Saudi Tourist e-Visa in 2026?',
    a: 'Yes. Saudi Tourist Visa for Indians is fully available in 2026. You can apply Saudi tourist visa online via visa.visitsaudi.com — Saudi tourist visa processing time India is just 5 minutes to 24 hours.',
  },
  {
    q: 'How long does Saudi tourist visa processing take for Indians?',
    a: 'e-Visa: 5 minutes to 24 hours. Visa on Arrival (US/UK/Schengen/GCC holders): 5–10 minutes at the airport. Sticker Visa (Saudi visa stamping India): 7–15 working days end-to-end.',
  },
  {
    q: 'What are the documents required for Saudi tourist visa from India?',
    a: 'Valid Indian passport (6+ months validity, 2 blank pages), passport photo (200×200 pixels on white background), confirmed return flight ticket, confirmed hotel booking or host invitation, 3-month bank statement with USD 750 quarterly average minimum balance, and travel insurance (included in the e-Visa application).',
  },
  {
    q: 'Can I perform Umrah on Saudi Tourist Visa?',
    a: 'Yes — but only outside Hajj season. The Saudi tourist visa allows Umrah from October to March. During Hajj season (April–May 2026), Umrah on a tourist visa is suspended.',
  },
  {
    q: 'What is the validity of the Saudi tourist visa 1 year validity option?',
    a: 'The e-Visa offers 1-year validity (365 days), allows up to 90 days per visit, and is a multiple entry tourist visa Saudi — unlimited entries within the 1-year validity window.',
  },
  {
    q: 'Is travel health insurance mandatory?',
    a: 'Yes. Valid medical insurance recognised in Saudi Arabia is mandatory and is included automatically as part of the e-Visa application — no separate purchase needed.',
  },
  {
    q: 'Can I extend my Saudi Tourist Visa while in Saudi Arabia?',
    a: 'Yes. Tourist visa holders can extend their stay up to 90 days through the official Saudi immigration portal, subject to approval and the visa\u2019s remaining validity.',
  },
  {
    q: 'Can I perform Hajj on Saudi Tourist Visa?',
    a: 'No. Hajj requires a separate Hajj permit and a designated Hajj visa. The Saudi tourist visa without sponsor for Indians is valid for tourism, family visits and Umrah (non-Hajj) only — not for Hajj.',
  },
];

const SaudiTouristVisaPage = () => {
  const ch = useContactChannel();

  usePageMeta(
    'Saudi Tourist Visa for Indians | MEA-Approved ZAFCO | 2026 Guide',
    'Get Saudi Tourist Visa for Indians with MEA-approved ZAFCO. 1-year validity, multiple-entry, 5–24 hours processing. Apply online today. Best Saudi visa agency in New Delhi.',
    {
      canonical: '/saudi-arabia/tourist-visa',
      image: '/social-media/zafco-facebook-cover.png',
      imageAlt: 'Saudi Tourist Visa for Indians — ZAFCO MEA-approved Saudi visa agency in New Delhi.',
    },
  );

  // FAQ schema (Google Rich Results eligible)
  useFAQSchema(FAQS);

  // Breadcrumb + Service JSON-LD
  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'Saudi Arabia', item: 'https://zafco.in/saudi-arabia' },
        {
          '@type': 'ListItem', position: 3,
          name: 'Saudi Tourist Visa for Indians',
          item: 'https://zafco.in/saudi-arabia/tourist-visa',
        },
      ],
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Saudi Tourist Visa Processing for Indians',
      description: 'MEA-approved Saudi Tourist Visa processing for Indian nationals — e-Visa (1-year multiple-entry), Visa on Arrival for US/UK/Schengen/GCC holders, and traditional sticker visa via Royal Saudi Embassy.',
      serviceType: 'Visa Processing',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'India' },
        { '@type': 'Country', name: 'Saudi Arabia' },
      ],
      audience: { '@type': 'Audience', audienceType: 'Indian tourists travelling to Saudi Arabia' },
      url: 'https://zafco.in/saudi-arabia/tourist-visa',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-saudi-tourist-page', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  const contactQuery = '?source=saudi-tourist-visa-india';
  const waMessage = 'Hello ZAFCO, I want to apply for a Saudi Tourist Visa from India. Please share next steps.';

  return (
    <div data-testid="saudi-tourist-visa-page">
      <PageHeader
        title="Saudi Tourist Visa for Indians — Complete 2026 Guide"
        subtitle="MEA-approved ZAFCO. 1-year validity, multiple entry, 5–24 hours e-Visa processing. Authorised Saudi tourist visa agents in India."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Saudi Arabia', path: '/saudi-arabia' },
          { label: 'Saudi Tourist Visa for Indians', path: '/saudi-arabia/tourist-visa' },
        ]}
      />

      {/* Government Approval Banner */}
      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved · Royal Saudi Embassy Empanelled</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* SECTION 1 — Introduction & Primary CTAs */}
      <section className="py-16 bg-white" aria-labelledby="saudi-tourist-intro">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Saudi Tourism · 2026</span>
          <h2 id="saudi-tourist-intro" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            India&rsquo;s premier tourism destination — process your Saudi tourist visa with ZAFCO
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Saudi Arabia has transformed into one of the world&rsquo;s most exciting tourism destinations, driven by Vision 2030. The Kingdom welcomes over <strong>2.5 million Indian tourists annually</strong> for leisure travel, family visits, cultural exploration, and Umrah (non-Hajj season).
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            The <strong>Saudi Tourist Visa for Indians</strong> is the official travel document that enables Indian nationals to enter Saudi Arabia for tourism, leisure, family visits, and Umrah (outside Hajj season) — without seeking employment or business. As of <strong>2026</strong>, Indian citizens can access the Saudi tourist e-Visa India (multiple-entry, 1-year validity) or sticker visa (single-entry) with streamlined processing via the official <em>visa.visitsaudi.com</em> portal.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link> is the best Saudi visa agency in New Delhi — your MEA-approved partner for Saudi tourism visas, with 15+ years of expertise and direct embassy empanelment. We are among the authorised Saudi tourist visa agents in India and Saudi visit visa stamping agents in Delhi. Looking for the Business Visit Visa instead? See our{' '}
            <Link to="/saudi-arabia/business-visa" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">Saudi Business Visit Visa for Indians</Link> guide.
          </p>

          {/* Primary CTAs */}
          <div className="flex flex-wrap gap-3 mt-10">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="saudi-tourist-hero-cta-apply">
              <Link to={`/contact${contactQuery}&intent=apply`}>
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Contact Experts Now
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue/5" data-testid="saudi-tourist-hero-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" aria-hidden="true" /> WhatsApp Us
              </a>
            </Button>
            <Button asChild variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50" data-testid="saudi-tourist-hero-cta-call">
              <a href={ch.phoneTelUrl}>
                <Phone className="w-4 h-4 mr-2" aria-hidden="true" /> Call {ch.phoneDisplay}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Eligibility */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-tourist-eligibility">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Eligibility</span>
          <h2 id="saudi-tourist-eligibility" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Saudi Tourist Visa eligibility for Indian citizens
          </h2>
          <h3 className="text-xl font-bold text-zafco-blue mb-4">Primary Eligibility Requirements</h3>
          <p className="text-gray-700 leading-relaxed mb-8">
            To qualify for a Saudi Tourist e-Visa for Indians, you must meet all of the following primary criteria.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {ELIGIBILITY.map((item) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <div>
                    <h3 className="font-bold text-zafco-blue mb-1">{item.label}</h3>
                    <p className="text-sm text-gray-700 leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
            <h3 className="text-lg font-bold text-zafco-blue mb-2 flex items-center gap-2">
              <Globe2 className="w-5 h-5 text-emerald-700" aria-hidden="true" />
              Saudi E-Visa for Indian Citizens with US/UK Visa
            </h3>
            <p className="text-sm text-gray-700">
              Indian citizens holding valid (used at least once) <strong>US, UK, or Schengen visas</strong> — or <strong>GCC residency</strong> — may qualify for the Saudi e-Visa for Indian citizens with US/UK visa (<strong>Visa on Arrival</strong>) at Saudi airports.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — Visa Options */}
      <section className="py-16 bg-white" aria-labelledby="saudi-tourist-options">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Visa Options · 2026</span>
          <h2 id="saudi-tourist-options" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-8">
            Types of tourist visa for Saudi Arabia available for Indians in 2026
          </h2>

          <div className="space-y-6">
            {VISA_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              return (
                <motion.div
                  key={opt.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 rounded-xl bg-zafco-blue/10 flex items-center justify-center">
                        <Icon className="w-6 h-6 text-zafco-blue" aria-hidden="true" />
                      </div>
                      <h3 className="font-bold text-zafco-blue text-lg">{opt.title}</h3>
                    </div>
                    <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full ${opt.badgeTone}`}>
                      {opt.badge}
                    </span>
                  </div>
                  <p className="text-gray-700 leading-relaxed mb-5">{opt.intro}</p>

                  <div className="grid md:grid-cols-2 gap-5">
                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-zafco-blue/5 text-zafco-blue">
                          <tr>
                            <th className="px-4 py-2.5 text-left font-bold">Feature</th>
                            <th className="px-4 py-2.5 text-left font-bold">Specification</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {opt.specs.map((row) => (
                            <tr key={row.k}>
                              <td className="px-4 py-2.5 font-semibold text-zafco-blue align-top">{row.k}</td>
                              <td className="px-4 py-2.5 text-gray-700">{row.v}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-white p-5">
                      <h4 className="font-bold text-zafco-blue text-sm mb-3 uppercase tracking-wider">Key Benefits</h4>
                      <ul className="space-y-2 text-sm text-gray-700">
                        {opt.benefits.map((b) => (
                          <li key={b} className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 4 — Documents Required */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-tourist-documents">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Document Checklist</span>
          <h2 id="saudi-tourist-documents" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            What are the documents required for Saudi tourist visa from India?
          </h2>
          <h3 className="text-xl font-bold text-zafco-blue mb-3">Essential Documents (Required for ALL e-Visa Applicants)</h3>
          <p className="text-gray-700 leading-relaxed mb-8">
            Complete, accurate documentation is the single biggest driver of approval. ZAFCO experts verify every document before submission.
          </p>

          <div className="grid md:grid-cols-2 gap-5">
            {DOCUMENT_GROUPS.map((group) => {
              const Icon = group.icon;
              return (
                <motion.div
                  key={group.title}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-zafco-blue/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-zafco-blue" aria-hidden="true" />
                    </div>
                    <h3 className="font-bold text-zafco-blue text-base">{group.title}</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {group.points.map((p) => (
                      <li key={p} className="flex items-start gap-2">
                        <ChevronRight className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTION 5 — Step-by-Step Process */}
      <section className="py-16 bg-white" aria-labelledby="saudi-tourist-process">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Application Process</span>
          <h2 id="saudi-tourist-process" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-8">
            How to get Saudi Tourist Visa for Indians — step-by-step
          </h2>

          <div className="space-y-5">
            {APPLICATION_PHASES.map((p, idx) => (
              <div key={p.phase} className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-zafco-blue text-white flex items-center justify-center font-bold text-sm">
                      {idx + 1}
                    </div>
                    <h3 className="font-bold text-zafco-blue text-lg">{p.phase}</h3>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-amber-700 bg-amber-100 px-2.5 py-1 rounded-full">
                    <Clock className="w-3.5 h-3.5" aria-hidden="true" /> {p.duration}
                  </span>
                </div>
                <ul className="space-y-2 text-sm text-gray-700">
                  {p.steps.map((s) => (
                    <li key={s} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — Processing Timeline */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-tourist-timeline">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Processing Time</span>
          <h2 id="saudi-tourist-timeline" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Saudi tourist visa processing time in India — 2026
          </h2>
          <p className="text-gray-700 mb-8">Standard processing windows across the three available routes.</p>

          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-zafco-blue/5 text-zafco-blue">
                <tr>
                  <th className="px-4 py-3 text-left font-bold">Route</th>
                  <th className="px-4 py-3 text-left font-bold">Applicable For</th>
                  <th className="px-4 py-3 text-left font-bold">Duration</th>
                  <th className="px-4 py-3 text-left font-bold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {TIMELINE.map((row) => (
                  <tr key={row.phase}>
                    <td className="px-4 py-3 font-semibold text-zafco-blue">{row.phase}</td>
                    <td className="px-4 py-3 text-gray-700">{row.activity}</td>
                    <td className="px-4 py-3 text-gray-700">{row.duration}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">{row.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-sm text-gray-600 mt-4 italic">
            Contact our experts for current express-processing availability and any seasonal advisories.
          </p>
        </div>
      </section>

      {/* SECTION 7 — Why ZAFCO */}
      <section className="py-16 bg-white" aria-labelledby="saudi-tourist-why-zafco">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Why ZAFCO</span>
          <h2 id="saudi-tourist-why-zafco" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
            Why choose ZAFCO — best Saudi visa agency in New Delhi?
          </h2>
          <h3 className="text-xl font-bold text-zafco-blue mb-3">Authorised Saudi tourist visa agents in India</h3>
          <p className="text-gray-700 leading-relaxed mb-8">
            ZAFCO Human Resource Management is among the leading Saudi tourist visa agents in India, Saudi visit visa stamping agents in Delhi, and Saudi visa consultants in South Delhi — directly empanelled with the Royal Saudi Embassy.
          </p>

          <div className="grid md:grid-cols-2 gap-4 mb-10">
            {WHY_ZAFCO.map((item) => (
              <div key={item.label} className="flex items-start gap-3 bg-slate-50 rounded-xl border border-slate-200 p-5">
                <BadgeCheck className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <div>
                  <h3 className="font-bold text-zafco-blue">{item.label}</h3>
                  <p className="text-sm text-gray-700">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="rounded-2xl bg-gradient-to-r from-zafco-blue to-zafco-blue/90 text-white p-6 md:p-8">
            <div className="grid md:grid-cols-3 gap-6 items-start">
              <div className="md:col-span-2">
                <h3 className="font-syne font-bold text-xl mb-2">Saudi visa agency near me — visit our New Delhi office</h3>
                <p className="text-white/90 text-sm leading-relaxed flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  GF 5, 27B, Khizrabad, New Friends Colony, New Delhi 110025 (South Delhi)
                </p>
                <p className="text-white/90 text-sm leading-relaxed flex items-start gap-2 mt-2">
                  <Phone className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <a href={ch.phoneTelUrl} className="hover:underline">{ch.phoneDisplay}</a>
                </p>
                <p className="text-white/90 text-sm leading-relaxed flex items-start gap-2 mt-2">
                  <Mail className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                  <a href={`mailto:${ch.email}`} className="hover:underline">{ch.email}</a>
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Button asChild className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold" data-testid="saudi-tourist-office-cta-apply">
                  <Link to={`/contact${contactQuery}&intent=apply`}>Contact Experts Now <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
                <Button asChild variant="outline" className="bg-white text-zafco-blue border-white hover:bg-white/90" data-testid="saudi-tourist-office-cta-whatsapp">
                  <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Consultation Form */}
      <section className="py-16 bg-slate-50 border-y border-slate-200" aria-labelledby="saudi-tourist-consultation">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="grid lg:grid-cols-2 gap-10 items-start">
            <div>
              <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Get Started</span>
              <h2 id="saudi-tourist-consultation" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
                Apply Saudi tourist visa from India today
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Incomplete documentation is the #1 reason for visa rejection. ZAFCO&rsquo;s MEA-approved experts review every document before submission to ensure 100% compliance with MOFA guidelines.
              </p>
              <ul className="space-y-2 text-sm text-gray-700 mb-6">
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Instant consultation with an MEA-approved executive</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Personalised eligibility review (e-Visa / Visa on Arrival / sticker)</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Transparent, up-to-date pricing — no hidden charges</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Hassle-free document pickup across New Delhi & pan-India</li>
                <li className="flex items-start gap-2"><CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /> Fastest e-Visa processing — typical 2–5 hour approval</li>
              </ul>
            </div>
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-theme="light">
              <ConsultationForm source="saudi-tourist-visa-india" />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 9 — FAQs */}
      <section className="py-16 bg-white" aria-labelledby="saudi-tourist-faqs">
        <div className="container mx-auto px-6 max-w-4xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">FAQs</span>
          <h2 id="saudi-tourist-faqs" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-8">
            Frequently asked questions — Saudi Tourist Visa for Indians
          </h2>
          <div className="space-y-3">
            {FAQS.map((item, idx) => (
              <details
                key={item.q}
                className="group rounded-xl border border-slate-200 bg-slate-50 open:bg-white open:shadow-sm transition-all"
                data-testid={`saudi-tourist-faq-${idx}`}
              >
                <summary className="cursor-pointer list-none flex items-start justify-between gap-3 p-5 font-semibold text-zafco-blue">
                  <span className="flex-1">Q{idx + 1}: {item.q}</span>
                  <ChevronRight className="w-5 h-5 flex-shrink-0 transition-transform group-open:rotate-90" aria-hidden="true" />
                </summary>
                <div className="px-5 pb-5 text-sm text-gray-700 leading-relaxed">
                  {item.a}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 10 — Final CTA */}
      <section className="py-16 bg-zafco-blue text-white" aria-labelledby="saudi-tourist-final-cta">
        <div className="container mx-auto px-6 max-w-4xl text-center">
          <h2 id="saudi-tourist-final-cta" className="text-3xl md:text-4xl font-bold font-syne mb-4">
            Contact ZAFCO — Saudi visa agency near me
          </h2>
          <p className="text-white/90 leading-relaxed mb-8 max-w-2xl mx-auto">
            Visa fees fluctuate based on visa type, MOFA updates, and exchange rates. Click &lsquo;Contact&rsquo; for exact, up-to-date pricing and personalised assistance — best Saudi visa agency in New Delhi, authorised Saudi tourist visa agents in India, Saudi visa consultants in South Delhi.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="bg-amber-500 hover:bg-amber-600 text-zafco-blue font-bold" data-testid="saudi-tourist-final-cta-apply">
              <Link to={`/contact${contactQuery}&intent=apply`}>
                Contact Experts Now <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild variant="outline" className="bg-white text-zafco-blue border-white hover:bg-white/90" data-testid="saudi-tourist-final-cta-whatsapp">
              <a href={ch.whatsappUrlWith(waMessage)} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp {ch.whatsappDisplay}
              </a>
            </Button>
            <Button asChild variant="outline" className="bg-transparent text-white border-white hover:bg-white/10" data-testid="saudi-tourist-final-cta-call">
              <a href={ch.phoneTelUrl}>
                <Phone className="w-4 h-4 mr-2" /> Call {ch.phoneDisplay}
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SaudiTouristVisaPage;
